<template>
  <div class="notepad">
    <div class="notepad-header">
      <notepad-header
        @filters-updated="onFiltersUpdated"
        @search-updated="onSearchUpdated"
        @add-note-click="showComposer = true"
        show-reading-mode-toggle
        show-search-and-filter-toolbar
        :show-add-note-button="hasRequiredReferenceFields"
        :custom-filter-form="customFilterForm"
        :add-note-disabled="addNoteDisabled"
      >
        <template #notepad-header>
          <slot
            name="notepad-header"
          />
        </template>

        <template #filter-form="props">
          <slot
            name="filter-form"
            v-bind="props"
          />
        </template>

      </notepad-header>
    </div>
    <div class="notepad-body">
      <el-collapse-transition>
        <note-composer
          v-if="showComposer"
          @save-note-click="onSaveNoteClick"
          @cancel="showComposer = false"
          :reference="reference"
          :custom-data-function="customDataFunction"
          :custom-creation-form="customCreationForm"
        >
          <template #creation-form="props">
            <slot
              name="creation-form"
              v-bind="props"
            />
          </template>
        </note-composer>
      </el-collapse-transition>
      <hr
        v-if="showComposer"
        class="u-m0">
      <notes-list
        :truncate-all-notes="isShortReadingMode"
        :notes="notes"
        :showActions="showActions"
        @note-click="onNoteClick"
        @edit-note-click="onEditNoteClick"
        @refresh-notes="onRefreshNotes"
        @scrollEnd="atScrollEnd"
      >
        <template #note-breadcrumb="props">
          <slot
            name="note-breadcrumb"
            v-bind="props"
          >
          </slot>
        </template>

        <template #note-subtitle="props">
          <slot
            name="note-subtitle"
            v-bind="props"
          />
        </template>
      </notes-list>
    </div>
  </div>
</template>

<style lang="scss">
@import "./_notepad.scss";
</style>

<script>
import NotesList from '#/notes/components/NotesList'
import NoteComposer from '#/notes/components/NoteComposer'
import NotepadHeader from '#/notes/components/NotepadHeader'
import {mapState, mapMutations, mapActions, mapGetters} from 'vuex'
import notesActions from '#/notes/store/actionTypes'
import notesMutations from '#/notes/store/mutationTypes'
import {NOTES_READING_MODE} from './definitions'
import {defaultPagination} from '#/notes/components/definitions'
import {isEqual} from 'lodash'

export default {
  name: 'NotepadList',
  data () {
    return {
      showComposer: false
    }
  },
  components: {
    NotesList,
    NoteComposer,
    NotepadHeader
  },
  props: {
    customDataFunction: {
      type: Function,
      required: true
    },
    customFilterFunction: {
      type: Function,
      required: true
    },
    reference: {
      type: Object,
      required: true
    },
    customCreationForm: {
      type: Object,
      required: true
    },
    customFilterForm: {
      type: Object,
      required: true
    },
    showActions: {
      type: Boolean,
      default: true
    },
    addNoteDisabled: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    'reference': {
      // Reference refers to an item of a domain. For example, a claim.
      handler (reference, oldReference) {
        // If there is no change, no update is required.
        if (isEqual(reference, oldReference)) return
        // If reference is not found, no updated is required.
        if (!reference) return
        // Reset user filters as there is change to reference or domain.
        // This is to prevent any out of context filter value remain active.
        this.resetUserFilters()
        this.resetUserFiltersMetadata()
        // Otherwise, fetch notes related to the reference or domain.
        this.togglePaginatedFetching(true)
        this.fetch()
      },
      immediate: true
    }
  },
  computed: {
    ...mapState('notes', [
      'readingMode'
    ]),
    ...mapGetters('notes', [
      'notes'
    ]),
    isShortReadingMode () {
      return this.readingMode === NOTES_READING_MODE.SHORT
    },
    referenceFilters () {
      // eslint-disable-next-line camelcase
      const {reference_id, reference_domain} = this.reference
      return {reference_id, reference_domain}
    },
    hasRequiredReferenceFields () {
      if (!this.reference) return false
      return !!(this.reference.reference_label && this.reference.reference_path && this.reference.reference_domain)
    }
  },
  methods: {
    ...mapMutations({
      resetUserFilters: notesMutations.RESET_USER_FILTERS,
      resetUserFiltersMetadata: notesMutations.RESET_USER_FILTERS_METADATA,
      togglePaginatedFetching: notesMutations.TOGGLE_PAGINATED_FETCHING,
      setReadingMode: notesMutations.SET_READING_MODE,
      updatePagination: notesMutations.UPDATE_PAGINATION
    }),
    ...mapActions({
      fetchNotes: notesActions.FETCH_NOTES,
      refreshNotes: notesActions.REFRESH_NOTES,
      addNote: notesActions.ADD_NOTE
    }),
    onRefreshNotes () {
      this.refreshNotes({referenceFilters: this.referenceFilters, customFilterFunction: this.customFilterFunction})
    },
    async fetch (config = {}) {
      try {
        await this.fetchNotes({
          ...config,
          referenceFilters: this.referenceFilters,
          customFilterFunction: this.customFilterFunction
        })
      } catch (error) {
        this.$message.error(error.message)
      }
      this.$emit('note-list-loaded')
    },
    async atScrollEnd () {
      this.fetch({reset: false})
    },
    onFiltersUpdated () {
      this.updatePagination(defaultPagination)
      this.fetch()
    },
    onSearchUpdated () {
      this.fetch()
    },
    onNoteClick (note) {
      this.$emit('note-click', note)
    },
    onEditNoteClick (note) {
      this.$emit('edit-note-click', note)
    },
    async onSaveNoteClick (note) {
      try {
        await this.addNote(note)
        this.$message.success('Note created successfully!')
      } catch (error) {
        this.$message.error(error.message)
      } finally {
        this.showComposer = false
      }
    }
  }
}
</script>
